import { Model } from 'pinia-orm';
import {
  Str,
  Attr,
  Uid,
  Num,
  Bool,
  HasOne,
  BelongsTo,
} from 'pinia-orm/decorators';
import { Section } from './Section';
import { User } from 'src/modules/User/models/User';
import type { UserSectionProgress } from 'types/course/section';
import type { ApiUserSectionUserSection } from 'src/types/contentTypes';
import type { StrapiAttr } from 'src/types/strapi';

/** Scalar field types are derived from the backend schema — see src/types/strapi.ts. */
type UserSectionAttr<K extends keyof ApiUserSectionUserSection['attributes']> =
  StrapiAttr<ApiUserSectionUserSection, K>;

export class UserSection extends Model {
  static override entity = 'user_sections';

  @Uid() declare id: number;
  @Str(null) declare documentId: string | null;

  // Strapi types these as DateTime (Date | string); over REST they always
  // arrive as ISO strings, so the narrower type is the accurate one.
  @Str(null) declare start_date: string | null;
  @Str(null) declare completion_date: string | null;
  // Not derived: the backend declares this as
  // `Integer & SetMinMax<{ min: '-1' }, string>`, and that string type argument
  // defeats Strapi's own value resolution (it yields `{}` rather than number).
  @Num(null) declare user_course_id: number;
  @Num(0) declare score: UserSectionAttr<'score'>;
  @Num(0) declare time_accumulator: UserSectionAttr<'time_accumulator'>;
  @Bool(true) declare is_assigned: UserSectionAttr<'is_assigned'>;
  @Str(null) declare unassigned_at: string | null;
  // Stored as JSON on the backend; the mutator below parses it into a shape
  // Strapi cannot describe, so this stays hand-typed.
  @Attr('{}') declare progress: UserSectionProgress | null;

  @HasOne(() => User, 'user_id') declare user_id: User | null;
  @Num(null) declare section_id: number | null;
  @BelongsTo(() => Section, 'section_id')
  declare section: Section | null;

  static override mutators() {
    return {
      progress: {
        get: (value: string | null) =>
          !value ? {} : (JSON.parse(value) as UserSectionProgress),
        set: (value: UserSectionProgress) => JSON.stringify(value),
      },
    };
  }
}
